import { useEffect, useMemo, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ExternalLink, Github, Play, FileText, Download } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

function getYouTubeId(url) {
  if (!url) return null;
  const match = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([\w-]+)/);
  return match ? match[1] : null;
}

export default function ProjectModal({ project, onClose }) {
  const { t } = useLanguage();
  if (!project) return null;
  const ytId = getYouTubeId(project.videoUrl);
  const previewImages = useMemo(() => {
    if (Array.isArray(project.screenshots) && project.screenshots.length > 0) {
      return project.screenshots;
    }

    return project.image ? [project.image] : [];
  }, [project.image, project.screenshots]);
  const hasImageCarousel = !ytId && previewImages.length > 1;
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const pointerStartXRef = useRef(null);

  useEffect(() => {
    setActiveImageIndex(0);
  }, [project.id]);

  useEffect(() => {
    if (!hasImageCarousel) return;

    previewImages.forEach((source) => {
      const image = new Image();
      image.src = source;
    });
  }, [hasImageCarousel, previewImages]);

  const goToPreviousImage = (event) => {
    event.stopPropagation();
    setActiveImageIndex((prev) => (prev - 1 + previewImages.length) % previewImages.length);
  };

  const goToNextImage = (event) => {
    event.stopPropagation();
    setActiveImageIndex((prev) => (prev + 1) % previewImages.length);
  };

  const handlePointerDown = (event) => {
    if (!hasImageCarousel) return;
    pointerStartXRef.current = event.clientX;
  };

  const handlePointerUp = (event) => {
    if (!hasImageCarousel || pointerStartXRef.current === null) return;

    const deltaX = event.clientX - pointerStartXRef.current;
    const swipeThreshold = 35;
    pointerStartXRef.current = null;

    if (deltaX > swipeThreshold) {
      setActiveImageIndex((prev) => (prev - 1 + previewImages.length) % previewImages.length);
    }

    if (deltaX < -swipeThreshold) {
      setActiveImageIndex((prev) => (prev + 1) % previewImages.length);
    }
  };

  const handlePointerCancel = () => {
    pointerStartXRef.current = null;
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
      onClick={onClose}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.9, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.9, y: 20 }}
        transition={{ type: 'spring', damping: 25, stiffness: 300 }}
        className="relative glass rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-10 w-10 h-10 rounded-full glass flex items-center justify-center text-heading hover:bg-accent-500/20 transition-colors"
        >
          <X size={20} />
        </button>

        {/* Media */}
        <div className="relative aspect-video rounded-t-2xl overflow-hidden" style={{ backgroundColor: 'var(--c-bg-secondary)' }}>
          {ytId ? (
            <iframe
              src={`https://www.youtube-nocookie.com/embed/${ytId}?rel=0`}
              title={project.title}
              className="w-full h-full"
              loading="lazy"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          ) : hasImageCarousel ? (
            <div
              className="relative w-full h-full touch-pan-y select-none cursor-grab active:cursor-grabbing"
              onPointerDown={handlePointerDown}
              onPointerUp={handlePointerUp}
              onPointerCancel={handlePointerCancel}
              onPointerLeave={handlePointerCancel}
            >
              <AnimatePresence initial={false}>
                <motion.img
                  key={`${project.id}-modal-${previewImages[activeImageIndex]}`}
                  src={previewImages[activeImageIndex]}
                  alt={`${project.title} preview ${activeImageIndex + 1}`}
                  className="absolute inset-0 w-full h-full object-cover"
                  loading="lazy"
                  decoding="async"
                  initial={{ opacity: 0.65, scale: 1.02 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.45, ease: 'easeOut' }}
                />
              </AnimatePresence>

              <button
                type="button"
                onClick={goToPreviousImage}
                className="absolute left-3 top-1/2 -translate-y-1/2 z-20 h-9 w-9 rounded-full bg-black/45 text-white text-lg leading-none hover:bg-black/60 transition-colors"
                aria-label={t.projects.previousPreview || 'Previous preview'}
              >
                ‹
              </button>
              <button
                type="button"
                onClick={goToNextImage}
                className="absolute right-3 top-1/2 -translate-y-1/2 z-20 h-9 w-9 rounded-full bg-black/45 text-white text-lg leading-none hover:bg-black/60 transition-colors"
                aria-label={t.projects.nextPreview || 'Next preview'}
              >
                ›
              </button>

              <div className="absolute bottom-3 left-1/2 -translate-x-1/2 z-20 flex items-center gap-2 rounded-full bg-black/45 px-3 py-1.5">
                {previewImages.map((source, idx) => (
                  <button
                    type="button"
                    key={source}
                    onClick={(event) => {
                      event.stopPropagation();
                      setActiveImageIndex(idx);
                    }}
                    className="relative inline-flex h-6 w-6 items-center justify-center rounded-full transition-colors hover:bg-white/10"
                    aria-label={`${t.projects.viewDetails} ${idx + 1}`}
                  >
                    <span
                      className={`h-1.5 rounded-full transition-all ${idx === activeImageIndex ? 'w-5 bg-white' : 'w-1.5 bg-white/60'}`}
                    />
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <>
              <img
                src={project.image}
                alt={project.title}
                className="w-full h-full object-cover"
                loading="lazy"
                decoding="async"
              />
              {!project.videoUrl && (
                <div className="absolute inset-0 flex items-center justify-center overlay-gradient">
                  <div className="text-center text-faint">
                    <Play size={48} className="mx-auto mb-2 opacity-30" />
                    <p className="text-sm">{t.projects.noVideo}</p>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Content */}
        <div className="p-6 sm:p-8">
          <p className="text-accent-400 text-xs font-mono mb-1">
            {project.subtitle}
          </p>
          <h3 className="text-heading text-2xl font-bold mb-3">
            {project.title}
          </h3>
          <p className="text-muted leading-relaxed mb-6">
            {project.longDescription || project.description}
          </p>

          {/* Tags */}
          <div className="flex flex-wrap gap-2 mb-6">
            {project.tags.map((tag) => (
              <span
                key={tag}
                className="px-3 py-1.5 rounded-lg text-xs font-medium bg-surface text-muted border border-surface-border"
              >
                {tag}
              </span>
            ))}
          </div>

          {/* Actions */}
          <div className="flex flex-wrap gap-3">
            {project.landingPage && (
              <a
                href={project.landingPage}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-linear-to-r from-accent-500 to-cyan-500 text-white font-medium text-sm hover:-translate-y-0.5 transition-transform"
              >
                <ExternalLink size={16} />
                {t.projects.viewLandingPage}
              </a>
            )}
            {project.demo && (
              <a
                href={project.demo}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-linear-to-r from-accent-500 to-cyan-500 text-white font-medium text-sm hover:-translate-y-0.5 transition-transform"
              >
                <ExternalLink size={16} />
                {t.projects.tryApps || t.projects.viewDemo}
              </a>
            )}
            {project.apk && (
              <a
                href={project.apk}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 px-5 py-2.5 rounded-xl border border-accent-400/40 text-accent-300 hover:bg-accent-500/10 font-medium text-sm transition-colors"
              >
                <Download size={16} />
                {t.projects.downloadApk}
              </a>
            )}
            {project.publication && (
              <a
                href={project.publication}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 px-5 py-2.5 rounded-xl border border-cyan-400/35 text-cyan-300 hover:bg-cyan-500/10 font-medium text-sm transition-colors"
              >
                <FileText size={16} />
                {t.projects.publication}
              </a>
            )}
            {project.code && (
              <a
                href={project.code}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 px-5 py-2.5 rounded-xl glass glass-hover text-heading font-medium text-sm"
              >
                <Github size={16} />
                {t.projects.viewCode}
              </a>
            )}
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}
